import { useEffect, useMemo, useRef, type ReactNode } from 'react'
import type { Diamond } from '../api/types'
import { StoneImage } from './StoneImage'
import { displayStockId, formatCarat, formatPrice, formatPerCarat } from '../lib/format'
import { stoneKey } from '../lib/stoneKey'
import { fluorescenceWord, formatMeasurements, gradeWord } from './StoneSpecs'
import { usePeerBands } from './StonePeer'
import { peerFor, peerSentence } from '../lib/peerBands'
import { feedback } from '../lib/feedback'
import './stoneCompare.css'

/**
 * COMPARE — the verb this screen was missing.
 *
 * ── WHY IT EXISTS ─────────────────────────────────────────────────────────
 * `grep -rn "compare" web/src` used to return nothing outside Gemma's server
 * side. The backend could compare stones; the client could not put two next to
 * each other. Selection existed on both shells and fed exactly one thing — the
 * broker share dialog. So the primary act of a stone list, "which of these",
 * had no implementation at all.
 *
 * ── THE ONE IDEA ──────────────────────────────────────────────────────────
 * Four cards side by side is not a comparison, it is four cards. What makes
 * this useful is the OPPOSITE of showing everything: rows where every pinned
 * stone agrees RECEDE, and only the rows that differ carry ink and an accent
 * rule. A nineteen-row spec table collapses to the four rows you are actually
 * choosing between. That is the whole design; everything else is plumbing.
 *
 * ── A DELIBERATE DEPARTURE FROM THE SPEC, ARGUED ──────────────────────────
 * The build spec asked for a cap of four pins, with a fifth firing
 * `feedback.deny()`. I did not implement the cap, because the pin set here is
 * the EXISTING share selection (`selectedKeys` on both shells), and the broker
 * share flow (#56) is deliberately uncapped — a broker routinely shares twenty
 * or thirty stones at their own markup. Capping that set at four to serve a
 * comparison panel would break a live revenue flow to add a refusal animation.
 *
 * The constraint the cap was protecting against — four columns are dead at
 * 320px — is real, and is solved properly instead: a frozen label gutter plus
 * snap-scrolling columns, which works at two pins and at twenty.
 *
 * ── WHAT IT IS NOT ────────────────────────────────────────────────────────
 * Columns are `.stone-card` (a solid, painted surface), never `.lqd-*`. Glass
 * is chrome, not a list item: four stacked `backdrop-filter`s is how you jank
 * a low-end Android, and this panel can hold more than four.
 */

type Cell = {
  text: string
  node?: ReactNode
  /**
   * How this value should READ ALOUD, when that differs from how it looks.
   * `EX` is a trade abbreviation and a screen reader pronounces it "ee ex".
   * Never part of the equality key — two stones that both say EX agree
   * whatever words we put on it.
   */
  spoken?: string
}

interface Row {
  label: string
  /** The label spelled out, for the row header's accessible name. */
  full: string
  /** The comparable value. Null = this stone has nothing to say here. */
  cell: (s: Diamond, mult: number) => Cell | null
}

/** A value plus how it should read aloud — text is also the equality key. */
const cell = (text: string | null | undefined, node?: ReactNode, spoken?: string): Cell | null =>
  text == null || text === '' ? null : { text, node, spoken }

/** A graded value: shown as the trade writes it, read as a human says it. */
const gradeCell = (raw: string | null | undefined): Cell | null => {
  const word = gradeWord(raw)
  return raw ? { text: raw, spoken: word ?? raw } : null
}

/**
 * The row vocabulary. Identical to the spec sheet's, in the trade's reading
 * order: identity, then the 4Cs, then finish, then proportions, then price.
 * Nothing here invents table %, girdle, crown or pavilion — we do not hold
 * them, and a comparison of four em-dashes is worse than their absence.
 *
 * ── THE ABBREVIATIONS ARE VISUAL ONLY ─────────────────────────────────────
 * The visible labels are the DESKTOP TABLE'S abbreviations, deliberately: one
 * vocabulary across the surfaces, and — the practical half — "Fluorescence"
 * and "Measurements" do not fit an 88px gutter, and widening the gutter costs
 * a whole column at 320px.
 *
 * This file used to claim in a comment that "screen readers get the value, not
 * the abbreviation". They got neither. The grid is `display: contents` rows
 * with no table semantics at all, so a screen reader read a flat run of values
 * with nothing saying WHICH ROW any of them was in — and where the value was
 * itself an abbreviation ("EX", "NON") it was spelled out letter by letter.
 * So: every row carries `full`, the grid is a real ARIA table (rowheader +
 * columnheader), and any value the trade abbreviates carries `spoken`.
 */
const ROWS: Row[] = [
  { label: 'Shape', full: 'Shape', cell: (s) => cell(s.shape) },
  { label: 'Carat', full: 'Carat weight', cell: (s) => cell(formatCarat(s.carat)) },
  { label: 'Colour', full: 'Colour grade', cell: (s) => cell(s.color) },
  { label: 'Clarity', full: 'Clarity grade', cell: (s) => cell(s.clarity) },
  {
    label: 'Cut',
    full: 'Cut grade',
    cell: (s) => (s.cut
      ? gradeCell(s.cut)
      : cell(s.specs?.cutGraded === false ? 'Not graded for this shape' : null)),
  },
  { label: 'Polish', full: 'Polish grade', cell: (s) => gradeCell(s.polish) },
  { label: 'Symmetry', full: 'Symmetry grade', cell: (s) => gradeCell(s.symmetry) },
  {
    label: 'Fluo',
    full: 'Fluorescence',
    cell: (s) => cell(s.fluorescence, undefined, fluorescenceWord(s.fluorescence) ?? undefined),
  },
  { label: 'Lab', full: 'Grading laboratory', cell: (s) => cell(s.lab) },
  { label: 'Cert no', full: 'Certificate number', cell: (s) => cell(s.certNo) },
  { label: 'Meas. (mm)', full: 'Measurements in millimetres', cell: (s) => cell(formatMeasurements(s)) },
  { label: 'Ratio', full: 'Length to width ratio', cell: (s) => cell(s.specs?.ratio != null ? s.specs.ratio.toFixed(2) : null) },
  { label: 'Depth', full: 'Depth percentage', cell: (s) => cell(s.specs?.depthPct != null ? `${s.specs.depthPct.toFixed(1)}%` : null) },
  { label: 'Growth', full: 'Growth method', cell: (s) => cell(s.stoneType) },
  { label: 'Location', full: 'Supplier location', cell: (s) => cell(s.origin) },
  { label: '$/ct', full: 'Price per carat', cell: (s, m) => cell(s.pricePerCarat != null ? formatPerCarat(Math.round(s.pricePerCarat * m)) : null) },
  { label: 'Total', full: 'Total price', cell: (s, m) => cell(s.priceUSD != null ? formatPrice(Math.round(s.priceUSD * m)) : null) },
]

export function StoneCompare({
  stones,
  onUnpin,
  onOpen,
  onClose,
  mult = 1,
  className = '',
}: {
  stones: Diamond[]
  onUnpin: (s: Diamond) => void
  /** Column header → this stone's detail. */
  onOpen?: (s: Diamond) => void
  onClose: () => void
  /** Aska Services fee multiplier, so the prices match the list behind it. */
  mult?: number
  className?: string
}) {
  const bands = usePeerBands()
  const rootRef = useRef<HTMLDivElement>(null)

  // Escape closes. Bound on the panel, not the document, so it cannot steal
  // the key from a modal opened on top of it.
  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose() } }
    el.addEventListener('keydown', onKey)
    return () => el.removeEventListener('keydown', onKey)
  }, [onClose])

  /**
   * The market-position row is built separately because its value is a
   * sentence derived from the band map, not a field. It is omitted entirely
   * when no pinned stone has a position — an empty row is a row of nothing.
   */
  const peerRow = useMemo(() => {
    const cells = stones.map((s) => {
      const p = peerFor(s, bands, mult)
      return p ? { text: peerSentence(p) } : null
    })
    return cells.some(Boolean) ? cells : null
  }, [stones, bands, mult])

  const rows = useMemo(() => {
    const built = ROWS.map((r) => {
      const cells = stones.map((s) => r.cell(s, mult))
      return { label: r.label, full: r.full, cells }
    })
    if (peerRow) built.push({ label: 'Position', full: 'Market position', cells: peerRow })
    // Drop rows no pinned stone can answer — four blanks is not information.
    return built.filter((r) => r.cells.some(Boolean))
  }, [stones, mult, peerRow])

  if (stones.length === 0) return null

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      role="region"
      aria-label={`Comparing ${stones.length} stone${stones.length === 1 ? '' : 's'}`}
      className={`compare-root ${className}`}
      data-testid="stone-compare"
    >
      <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink">Compare</p>
          <p className="text-[11px] text-navy-300">
            {stones.length === 1
              ? 'Pin another stone to compare'
              : 'Rows that differ are highlighted'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => { feedback.dim(); onClose() }}
          aria-label="Close compare"
          data-testid="compare-close"
          className="inline-flex h-11 w-11 items-center justify-center rounded-full text-navy-300 hover:bg-navy-100/40 hover:text-ink transition-colors duration-micro"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* A single scroller holding the header strip and the grid, so the label
          gutter freezes over BOTH and the columns cannot desynchronise. */}
      <div className="compare-scroll" data-testid="compare-scroll">
        {/* A real ARIA table, not just a grid that looks like one. Without the
            roles a screen reader reads a flat run of values with nothing
            saying which row or which stone any of them belongs to; with them,
            every cell is announced against its row header and its column
            header. `display: contents` (the `.contents` wrappers) keeps the
            CSS grid layout untouched. */}
        <div
          className="compare-grid"
          role="table"
          aria-label={`Comparison of ${stones.length} stone${stones.length === 1 ? '' : 's'}`}
          style={{ '--cols': stones.length } as React.CSSProperties}
        >
          {/* ── Column heads ─────────────────────────────────────────── */}
          <div className="contents" role="row">
          <div className="compare-gutter compare-head" role="columnheader" aria-label="Specification" />
          {stones.map((s) => (
            <div key={stoneKey(s)} className="compare-col compare-head stone-card" role="columnheader">
              <button
                type="button"
                onClick={() => onOpen?.(s)}
                className="flex w-full min-w-0 items-center gap-2 rounded-md p-1 text-left transition-colors duration-micro hover:bg-gold-050"
                data-testid="compare-open"
                aria-label={`Open ${displayStockId(s)}`}
              >
                <span className="relative block h-10 w-10 shrink-0 overflow-hidden rounded-md ring-1 ring-line bg-gradient-to-br from-cream to-[#eef1f6]">
                  <StoneImage stone={s} badge={false} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate mono-id text-[11px] font-semibold text-ink">#{displayStockId(s)}</span>
                  <span className="block truncate text-[10.5px] text-navy-300">{s.shape} · {formatCarat(s.carat)}</span>
                </span>
              </button>
              <button
                type="button"
                onClick={(e) => { feedback.dim(e.currentTarget.closest('.compare-col')); onUnpin(s) }}
                aria-label={`Remove ${displayStockId(s)} from compare`}
                data-testid="compare-unpin"
                className="mt-1 inline-flex h-8 w-full items-center justify-center rounded-full text-[10.5px] font-semibold uppercase tracking-[0.08em] text-navy-300 hover:bg-navy-100/40 hover:text-ink transition-colors duration-micro"
              >
                Unpin
              </button>
            </div>
          ))}
          </div>

          {/* ── The rows ─────────────────────────────────────────────── */}
          {rows.map((r) => {
            // "Differs" means: the pinned stones do not all say the same thing,
            // counting a missing value as its own answer. This is the entire
            // reason the panel is worth opening.
            const first = r.cells[0]?.text ?? null
            const differs = r.cells.some((c) => (c?.text ?? null) !== first)
            return (
              <div key={r.label} className="contents" role="row" data-testid="compare-row" data-differs={differs}>
                <div
                  className={`compare-gutter compare-cell ${differs ? 'is-diff' : ''}`}
                  role="rowheader"
                  // The visible text is the trade's abbreviation; the
                  // accessible name is the word. "Fluo" reads as "floo".
                  aria-label={r.full}
                >
                  {differs && <span className="compare-tick" aria-hidden />}
                  <span className="compare-label" title={r.full}>{r.label}</span>
                </div>
                {r.cells.map((c, i) => (
                  <div
                    key={`${r.label}:${stoneKey(stones[i])}`}
                    className={`compare-col compare-cell ${differs ? 'is-diff' : ''}`}
                    role="cell"
                  >
                    {c
                      ? (c.spoken && c.spoken !== c.text
                        ? <>
                            <span aria-hidden="true">{c.node ?? c.text}</span>
                            <span className="sr-only">{c.spoken}</span>
                          </>
                        : (c.node ?? c.text))
                      : <span className="sr-only">Not stated</span>}
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
